import { AnvaQuote, AnvaCoverage } from '@flows/src/types/anva';
import { AnvaQuoteSortType } from '@flows/src/pages/flow-products/AnvaFlowProductsOverview';
import { AnvaFlowsInsuranceTag } from '@flows/src/api/anva-flows/getAnvaFlowsInsuranceTags';

export type AnvaGroupSortType = AnvaQuoteSortType;

export type CoverageCombination = string;

export type GroupedAnvaQuote = {
  key: string;
  risk_object_name: string;
  anva_product_id: string;
  index: number;
  tag: Omit<AnvaFlowsInsuranceTag, 'insurance_ids'>;
  quotes: AnvaQuote[];
  sharedCoverages: AnvaCoverage[];
  uniqueCoverages: AnvaCoverage[];
  isGroupLoading: boolean;
  /** "horizontal" sorting of quotes within a group */
  sortedQuotes: AnvaQuote[];
  coverageCombinations: CoverageCombination[];
};

export type AnvaQuoteGroupingResult = {
  groups: GroupedAnvaQuote[];
  allCoverageCombinations: CoverageCombination[];
};

/**
 * Groups Anva quotes by anva_product_id and index, calculates shared/unique coverages,
 * and provides sorting functionality
 */
export function groupAnvaQuotes(quotes: AnvaQuote[]): AnvaQuoteGroupingResult {
  // Group quotes by anva_product_id and index
  const groupedByAnvaProductAndIndex = quotes.reduce<
    Record<string, AnvaQuote[]>
  >((acc, quote) => {
    const key = `${quote.anva_product_id}.${quote.index}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(quote);
    return acc;
  }, {});

  // Transform groups into GroupedAnvaQuote objects
  const groups: GroupedAnvaQuote[] = Object.entries(
    groupedByAnvaProductAndIndex,
  ).map(([key, quotes]) => {
    const [anva_product_id, indexStr] = key.split('.');
    const index = parseInt(indexStr, 10);

    const sharedCoverages = getSharedCoverages(quotes);
    const uniqueCoverages = getUniqueCoverages(quotes);
    const isGroupLoading = quotes.some((quote) => quote.isLoading);
    const coverageCombinations = getCoverageCombinations(quotes);

    // Sort quotes within group by number of selected coverages, then by combinationCode
    const sortedQuotes = quotes.slice().sort((a, b) => {
      const distinctCoveragesA = getDistinctCoveragesForQuote(
        a,
        sharedCoverages,
      );
      const distinctCoveragesB = getDistinctCoveragesForQuote(
        b,
        sharedCoverages,
      );

      // First sort by coverage count (ascending)
      const coverageCountDiff =
        distinctCoveragesA.length - distinctCoveragesB.length;
      if (coverageCountDiff !== 0) {
        return coverageCountDiff;
      }

      // If same coverage count, sort by combination identifier
      const combinationA =
        createCoverageCombinationIdentifier(distinctCoveragesA);
      const combinationB =
        createCoverageCombinationIdentifier(distinctCoveragesB);
      return combinationA.localeCompare(combinationB);
    });

    // Generate display name for the group
    const risk_object_name = getGroupRiskObjectName(quotes, index);

    return {
      key,
      anva_product_id,
      index,
      tag: quotes[0].tag,
      quotes,
      sharedCoverages,
      uniqueCoverages,
      isGroupLoading,
      sortedQuotes,
      risk_object_name,
      coverageCombinations,
    };
  });

  // Collect all unique coverage combinations across all groups
  const allCoverageCombinations = getAllCoverageCombinations(groups);

  return {
    groups,
    allCoverageCombinations,
  };
}

/**
 * Gets coverage combinations for quotes in a group
 */
function getCoverageCombinations(quotes: AnvaQuote[]): CoverageCombination[] {
  const combinations: CoverageCombination[] = [];

  quotes.forEach((quote) => {
    // Create a combination based on coverages that match what's shown in the quote choice
    const sharedCoverages = getSharedCoverages(quotes);
    const distinctCoverages = getDistinctCoveragesForQuote(
      quote,
      sharedCoverages,
    );

    const combinationIdentifier =
      createCoverageCombinationIdentifier(distinctCoverages);

    // Check if this combination already exists
    const existingCombination = combinations.find(
      (c) => c === combinationIdentifier,
    );

    if (!existingCombination) {
      combinations.push(combinationIdentifier);
    }
  });
  return combinations;
}

/**
 * Creates a coverage combination string from a quote's coverages
 */
export function createCoverageCombinationIdentifier(
  coverages: AnvaCoverage[],
): string {
  const sortedCoverages = coverages.sort((a, b) => {
    // Put mandatory coverages first
    if (a.mandatory && !b.mandatory) return -1;
    if (!a.mandatory && b.mandatory) return 1;

    // If both are mandatory or both are not mandatory, sort alphabetically
    const codeA = a.name;
    const codeB = b.name;
    return codeA.localeCompare(codeB);
  });

  const combinationIdentifier = sortedCoverages.map((c) => c.name).join(' + ');

  return combinationIdentifier;
}

/**
 * Gets all unique coverage combinations across all groups
 */
function getAllCoverageCombinations(
  groups: GroupedAnvaQuote[],
): CoverageCombination[] {
  const allCombinations = new Set<string>();
  groups.forEach((group) => {
    group.coverageCombinations.forEach((combinationIdentifier) => {
      if (
        combinationIdentifier &&
        group.quotes.length > 1 &&
        !group.isGroupLoading
      ) {
        allCombinations.add(combinationIdentifier);
      }
    });
  });
  return Array.from(allCombinations);
}

/**
 * Gets coverages that are shared across all quotes in a group
 */
function getSharedCoverages(data: AnvaQuote[]): AnvaCoverage[] {
  const flatCoverages = data
    .map((quote) => quote.coverages)
    .flat()
    // only pick selected coverages and non-mandatory coverages
    .filter((coverage) => coverage.selected && !coverage.mandatory);

  const uniqueCoverageCodes = [
    ...new Set(flatCoverages.map((coverage) => coverage.code)),
  ];

  // Create a Map to avoid duplicates and get coverage objects
  const coverageMap = new Map(
    flatCoverages.map((coverage) => [coverage.code, coverage]),
  );

  // return only coverages present in all quotes
  return uniqueCoverageCodes
    .filter((coverageCode) => {
      return data.every((quote) =>
        quote.coverages.some(
          // Some typing mismatch here, converting both to string to make filter work, to investigate
          (c) => String(c.code) === String(coverageCode) && c.selected,
        ),
      );
    })
    .map((coverageCode) => coverageMap.get(coverageCode)!);
}

/**
 * Gets all unique coverages across quotes in a group
 */
function getUniqueCoverages(data: AnvaQuote[]): AnvaCoverage[] {
  const flatCoverages = data
    .map((quote) => quote.coverages)
    .flat()
    .filter((coverage) => coverage.selected);

  const uniqueCoverageCodes = [
    ...new Set(flatCoverages.map((coverage) => coverage.code)),
  ];

  // Create a Map to avoid duplicates and get coverage objects
  const coverageMap = new Map(
    flatCoverages.map((coverage) => [coverage.code, coverage]),
  );

  return uniqueCoverageCodes.map(
    (coverageCode) => coverageMap.get(coverageCode)!,
  );
}

/**
 * Gets coverages for a specific quote, excluding shared coverages
 * Used for displaying individual quote coverages in grouped views
 */
export function getDistinctCoveragesForQuote(
  quote: AnvaQuote,
  sharedCoverages: AnvaCoverage[],
): AnvaCoverage[] {
  const nonSharedCoverages = quote.coverages.filter(
    (coverage) => !sharedCoverages.some((c) => c.code === coverage.code),
  );

  // list has all coverages in it, only selected are shown
  // also remove mandatory coverages, they need to be shown first in the list
  const selectedNonSharedCoverages =
    nonSharedCoverages?.filter(
      (coverage) => coverage.selected && !coverage.mandatory,
    ) || [];

  const mandatoryCoverages = quote.coverages.filter(
    (coverage) => coverage.mandatory,
  );

  return [...mandatoryCoverages, ...selectedNonSharedCoverages];
}

/**
 * Gets risk object name for a group of quotes
 */
function getGroupRiskObjectName(quotes: AnvaQuote[], index: number) {
  if (quotes.length === 0) return '';

  // First try to get risk_object_name from any quote in the group
  const quoteWithRiskObjectName = quotes.find(
    (quote) => quote.risk_object_name,
  );
  return quoteWithRiskObjectName?.risk_object_name || '';
}
